/**
 * The Dashboard's pet roster: one page of pets with the status, timestamp, adopter snapshot
 * and chip the table shows, all resolved by the server.
 *
 * Replaces a merge the Dashboard used to perform in the browser — every pet in the household,
 * cross-referenced against three drained `rescueDevices` filters and a drain of the reseller's
 * registered chips — to derive a status column and then filter, sort and slice it.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  rescueRosterQuery,
  type RescueRosterData,
  type RescueRosterRowGql,
  type RescueRosterSortKey,
  type RescueRosterStatusFilter,
} from '../lib/rescueQueries';
import type { SortDir } from '../lib/types';
import type { PetStatus } from '../lib/utils';
import type { PetRow } from '../pages/Dashboard/PetsTable';

/** Columns the roster table can be sorted by. */
export type RosterSortKey = 'name' | 'chip' | 'device' | 'status';

const SORT_KEY_TO_GQL: Record<RosterSortKey, RescueRosterSortKey> = {
  name: 'NAME',
  chip: 'CHIP',
  device: 'DEVICE',
  status: 'STATUS',
};

const STATUS_FROM_GQL: Record<RescueRosterRowGql['status'], PetStatus> = {
  RESCUE: 'rescue',
  FOSTER: 'foster',
  PENDING_ADOPTION: 'pending_adoption',
  ADOPTED: 'adopted',
};

/**
 * The chip filters the Dashboard offers. `adopted` deliberately covers pending adoptions too —
 * the tab reads "Adopted" and has always meant "on its way out or gone". `no_device` is a
 * different axis and rides on `hasDevice` rather than being a fifth status.
 */
export type RosterFilter = 'rescue' | 'foster' | 'adopted' | 'no_device';

const STATUS_FILTER_TO_GQL: Record<'rescue' | 'foster' | 'adopted', RescueRosterStatusFilter> = {
  rescue: 'RESCUE',
  foster: 'FOSTER',
  adopted: 'ADOPTED',
};

/** A roster row plus the adopter snapshot, which the add-chip modal needs and `PetRow` has no slot for. */
export interface RosterPet extends PetRow {
  pendingAdoption: { fullName: string | null; email: string | null; phone: string | null } | null;
}

/**
 * Exported so the chip preference can be exercised directly: which of the two chips a row
 * shows is the whole reason the reseller-chip drain existed, and a regression there renders a
 * plausible empty cell rather than an error.
 */
export function adaptRosterRow(row: RescueRosterRowGql): RosterPet {
  const { pet } = row;
  // `row.chip` is the pet-matched one, which survives the household membership ending;
  // `pet.chip` goes null there. Preferring the row's means a pending or adopted pet keeps
  // showing the chip the rescue still owns.
  //
  // The fallback does not fire today: `row.chip` resolves through a bare lookup by pet, while
  // `pet.chip` additionally filters on the chip being Working and on read authorisation, so
  // the row's is a strict superset. It stays as belt-and-braces against that changing, and
  // both selections carry `isFiChip` so the chip menu gates the same way whichever answers.
  const chip = row.chip ?? pet.chip ?? null;
  return {
    ...pet,
    chip,
    device: pet.device ?? null,
    hasDevice: !!pet.device,
    hasChip: !!chip,
    status: STATUS_FROM_GQL[row.status],
    // The status-appropriate timestamp: the transfer's for a placement or adoption, the pet's
    // own otherwise. Picked server-side, where the transfer that decided the status is.
    updatedAt: row.updatedAt,
    pendingAdoption: row.pendingAdoption
      ? {
          fullName: row.pendingAdoption.toFullName,
          email: row.pendingAdoption.toEmail,
          phone: row.pendingAdoption.toPhone,
        }
      : null,
  };
}

export interface UseRescueRosterResult {
  pets: RosterPet[];
  /** Every pet matching the search and filter, not just the page. */
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  /**
   * Showing the last page while the next is in flight. Distinct from `loading`, which means
   * there is nothing on screen yet: this one asks the table to dim what it has rather than
   * replace it with skeletons.
   */
  transitioning: boolean;
  error: boolean;
  /** Re-runs the query — the retry behind the table's error row. */
  refetch: () => void;
  goToPage: (page: number) => void;
}

export default function useRescueRoster(
  searchQuery: string,
  filter: RosterFilter | null,
  pageSize: number,
  sort: { key: RosterSortKey; dir: SortDir } | null,
): UseRescueRosterResult {
  const [page, setPage] = useState(1);

  // Search, filter, sort and page size each re-cut the list, so the page number the user is on
  // no longer points at what they were reading. Back to the top rather than a mid-list slice.
  //
  // Adjusted during render rather than in an effect, and read through a local the rest of this
  // render uses. An effect lands a render later, after `variables` below has already sent the
  // old offset under the new cut — a page nobody will see, from a query the server answers with
  // joins and a count.
  const signature = `${searchQuery}|${filter}|${pageSize}|${sort ? `${sort.key}:${sort.dir}` : 'none'}`;
  const [prevSignature, setPrevSignature] = useState(signature);
  let currentPage = page;
  if (prevSignature !== signature) {
    setPrevSignature(signature);
    setPage(1);
    currentPage = 1;
  }

  const variables = useMemo(() => {
    const input: Record<string, unknown> = { limit: pageSize, offset: (currentPage - 1) * pageSize };
    if (searchQuery) {
      input.query = searchQuery;
    }
    if (filter === 'no_device') {
      input.hasDevice = false;
    } else if (filter) {
      input.status = STATUS_FILTER_TO_GQL[filter];
    }
    // Omitted rather than nulled: the schema's own default is STATUS descending, which is what
    // the table opens on, so there is nothing to say until the user picks a different column.
    if (sort) {
      input.sortKey = SORT_KEY_TO_GQL[sort.key];
      input.sortDirection = sort.dir === 'desc' ? 'DESC' : 'ASC';
    }
    return { input };
  }, [searchQuery, filter, pageSize, currentPage, sort]);

  const { data, previousData, loading, error, refetch } = useQuery<RescueRosterData>(rescueRosterQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  // Which cut the last successful answer belongs to. `previousData` is Apollo's last success
  // whatever it was asked for, so after a failure it can be a page of a different search,
  // filter, sort or page number.
  const cut = `${signature}|${currentPage}`;
  const answeredCut = useRef<string | null>(null);
  if (data) {
    answeredCut.current = cut;
  }

  // Keep the last page on screen while the next is in flight, so re-sorting or typing dims the
  // table rather than emptying it — but only while it still answers the question being asked.
  //
  // A refetch that fails leaves the rows valid: same cut, we simply could not revalidate, and
  // dropping them would empty a table mid-task over one bad response. A fetch for a *new* cut
  // that fails leaves them answering the cut before it, and rendering them says the search or
  // filter returned these rows. Handing back nothing is what lets the table say it failed —
  // its error row yields to rows, by design, so stale ones would suppress it outright.
  const stale = !data && answeredCut.current !== cut;
  const result = stale && error ? undefined : (data ?? previousData)?.rescueRoster;
  const totalCount = result?.totalCount ?? 0;

  const pets = useMemo(() => (result?.items ?? []).map(adaptRosterRow), [result?.items]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Removing the last pet on the last page shrinks the roster under the user: the count drops,
  // the page they are on stops existing, and nothing on screen says so — `Pagination` renders
  // null at one page, so what is left is "No pets found" with no control to get back. The
  // signature reset above does not cover it: search, filter, sort and page size are all
  // unchanged. Only once a result is in hand, since an initial load reports zero for want of
  // an answer rather than because the roster shrank.
  if (result && currentPage > totalPages) {
    setPage(totalPages);
    currentPage = totalPages;
  }

  const goToPage = useCallback((target: number) => {
    setPage(Math.max(1, target));
  }, []);

  return {
    pets,
    totalCount,
    currentPage,
    totalPages,
    // Only a loading state worth showing skeletons for: with `previousData` in hand the table
    // keeps its rows through a revalidation.
    loading: loading && !data && !previousData,
    // Stale rows on screen with the current cut still unanswered — the one case the table has
    // something to dim.
    transitioning: loading && !data && !!previousData,
    error: !!error,
    refetch: () => {
      // Swallowed deliberately: the failure is already on `error`, which is what the table's
      // retry row renders from. An escaping rejection would only report it a second time.
      refetch().catch(() => {});
    },
    goToPage,
  };
}
